import React from 'react';
import { ChartTooltipData } from '../../../types/types';

interface TooltipProps {
  data: ChartTooltipData;
}

/**
 * Chart Tooltip Component
 */
export const ChartTooltip: React.FC<TooltipProps> = ({ data }) => {
  // Calculate tooltip position
  const tooltipStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${data.position.x}px`,
    top: `${data.position.y - 10}px`,
    transform: 'translate(-50%, -100%)',
  };

  return (
    <div className="simple-chart-tooltip" style={tooltipStyle}>
      <div className="simple-chart-tooltip-header">{data.xLabel}</div>
      <div className="simple-chart-tooltip-content">
        {data.series.map(series => (
          <div key={series.id} className="simple-chart-tooltip-row">
            <div className="simple-chart-tooltip-color" style={{ backgroundColor: series.color }} />
            <div className="simple-chart-tooltip-label">{series.name}:</div>
            <div className="simple-chart-tooltip-value">{series.formattedValue}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Find closest X value to mouse position
 */
export const findClosestXValue = (
  mouseX: number,
  xValues: (string | number)[],
  xScale: (value: string | number) => number
) => {
  let closestXIndex = 0;
  let minDistance = Infinity;

  xValues.forEach((xValue, index) => {
    const xPos = xScale(xValue);
    const distance = Math.abs(xPos - mouseX);
    if (distance < minDistance) {
      minDistance = distance;
      closestXIndex = index;
    }
  });

  return xValues[closestXIndex];
};

/**
 * Generate tooltip data for a given X value
 */
export const generateTooltipData = (
  xValue: string | number,
  seriesData: Array<{
    id: string;
    name: string;
    color: string;
    data: Array<{ x: string | number; y: number }>;
  }>,
  xPos: number,
  yScale: (value: number) => number,
  formatValue: (value: number) => string
): ChartTooltipData => {
  // Get values for all series at this x position
  const tooltipSeriesData = seriesData.map(s => {
    const point = s.data.find((p: { x: string | number }) => p.x === xValue);
    return {
      id: s.id,
      name: s.name,
      value: point?.y || 0,
      color: s.color,
      formattedValue: point ? formatValue(point.y) : '0',
    };
  });

  return {
    x: xValue,
    xLabel: String(xValue),
    series: tooltipSeriesData,
    position: {
      x: xPos,
      y: Math.min(...tooltipSeriesData.map(s => yScale(s.value))),
    },
  };
};
